import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBasket, Truck, Percent, Mail, Check, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const SEEN_KEY = "arbe_welcome_seen_v1";
const DELAY_MS = 2500;

/**
 * Grand-opening welcome popup with notify-me email capture.
 * - Only shows once per browser (localStorage)
 * - Only shows to guests (not logged-in users)
 * - Email captured -> POST /api/marketing/notify-signup
 */
export default function WelcomePopup() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) return;
    try {
      if (localStorage.getItem(SEEN_KEY)) return;
    } catch (_) { /* noop */ }
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [user]);

  const close = () => {
    try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch (_) { /* noop */ }
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/marketing/notify-signup", { email, phone, source: "welcome_popup" });
      setDone(true);
      try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch (_) { /* noop */ }
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open || user) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-sm animate-in fade-in"
      onClick={close}
      data-testid="welcome-popup"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom sm:zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-[#F1F3EE] transition-colors z-10"
          aria-label="Close welcome message"
          data-testid="welcome-popup-close"
        >
          <X className="h-4 w-4 text-[#4A5D5A]" />
        </button>

        <div className="h-36 bg-gradient-to-br from-[#2F5233] via-[#1F3D24] to-[#2F5233] relative overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: "radial-gradient(circle at 20% 50%, #F3C97A 1px, transparent 1px), radial-gradient(circle at 70% 30%, #F3C97A 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }} />
          <div className="relative h-20 w-20 rounded-full bg-white flex items-center justify-center shadow-lg">
            <img src="/assets/arbe-logo.png" alt="Arbe Minimart" className="h-14 w-14 object-contain" />
          </div>
        </div>

        <div className="px-6 pt-6 pb-6 text-center">
          {done ? (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-[#2F5233]/10 flex items-center justify-center">
                <Check className="h-7 w-7 text-[#2F5233]" />
              </div>
              <h2 className="font-heading font-extrabold text-2xl text-[#1C2826] mt-3 leading-tight">
                Salamat! You&apos;re on the list.
              </h2>
              <p className="mt-2 text-sm text-[#4A5D5A] leading-relaxed">
                We&apos;ll email you the moment Arbe Minimart opens on
                <span className="font-bold text-[#C62828]"> May 28, 2026</span>. See you then!
              </p>
              <button
                onClick={close}
                className="mt-6 w-full bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-full font-bold py-3 text-sm transition-colors"
                data-testid="welcome-popup-done"
              >
                Continue browsing
              </button>
            </>
          ) : (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C62828]">Grand Opening · May 28, 2026</div>
              <h2 className="font-heading font-extrabold text-2xl text-[#1C2826] mt-1 leading-tight">
                <span className="text-[#C62828]">Arbe Minimart</span> opens soon!
              </h2>
              <p className="mt-3 text-sm text-[#4A5D5A] leading-relaxed">
                Drop your email and we&apos;ll notify you the moment we open — plus you&apos;ll get an
                <span className="font-bold text-[#2F5233]"> opening-day discount</span>.
              </p>

              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                <div className="p-1.5">
                  <div className="mx-auto h-9 w-9 rounded-full bg-[#F1F3EE] flex items-center justify-center">
                    <ShoppingBasket className="h-4.5 w-4.5 text-[#2F5233]" />
                  </div>
                  <div className="text-[10px] font-semibold mt-1 text-[#1C2826] leading-tight">Farm-fresh</div>
                </div>
                <div className="p-1.5">
                  <div className="mx-auto h-9 w-9 rounded-full bg-[#F1F3EE] flex items-center justify-center">
                    <Truck className="h-4.5 w-4.5 text-[#E67E22]" />
                  </div>
                  <div className="text-[10px] font-semibold mt-1 text-[#1C2826] leading-tight">Same-day</div>
                </div>
                <div className="p-1.5">
                  <div className="mx-auto h-9 w-9 rounded-full bg-[#F1F3EE] flex items-center justify-center">
                    <Percent className="h-4.5 w-4.5 text-[#C62828]" />
                  </div>
                  <div className="text-[10px] font-semibold mt-1 text-[#1C2826] leading-tight">Free over ₱1,000</div>
                </div>
              </div>

              <form onSubmit={submit} className="mt-5 space-y-2">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#738380]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-[#E1E5DF] rounded-full focus:outline-none focus:ring-2 focus:ring-[#2F5233]/40"
                    data-testid="welcome-popup-email"
                    required
                  />
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Mobile (optional, for SMS reminder)"
                  className="w-full px-3 py-2.5 text-sm border border-[#E1E5DF] rounded-full focus:outline-none focus:ring-2 focus:ring-[#2F5233]/40"
                  data-testid="welcome-popup-phone"
                />
                {error && <div className="text-[11px] text-[#C62828] text-left px-3">{error}</div>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C62828] hover:bg-[#A91D1D] disabled:opacity-60 text-white rounded-full font-bold py-3 text-sm transition-colors flex items-center justify-center gap-2"
                  data-testid="welcome-popup-cta"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {loading ? "Submitting..." : "Notify me on opening day"}
                </button>
              </form>

              <div className="mt-3 flex items-center justify-center gap-3 text-[11px] text-[#738380]">
                <Link to="/register" onClick={close} className="hover:text-[#1C2826] underline" data-testid="welcome-popup-register">
                  Create account instead
                </Link>
                <span>·</span>
                <button
                  onClick={close}
                  className="hover:text-[#1C2826]"
                  data-testid="welcome-popup-browse"
                >
                  Maybe later
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
